import { styled, alpha } from '@mui/material/styles';
import { Box, Paper, TableCell } from '@mui/material';

/**
 * ページコンテナ
 */
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

/**
 * ヘッダーセクション
 */
export const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

/**
 * ページヘッダー行（全ボタン横一列）
 */
export const PageHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  padding: theme.spacing(1, 0),
}));

/**
 * 月ナビゲーションエリア
 */
export const MonthNavigation = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

/**
 * ヘッダーボタングループ（区切り線付き）
 */
export const HeaderButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

/**
 * タイトルエリア
 */
export const TitleArea = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

/**
 * アクションボタンエリア
 */
export const ActionButtonsArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

/**
 * カレンダーコンテナ
 */
export const CalendarContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

/**
 * カレンダーヘッダー
 */
export const CalendarHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
}));

/**
 * カレンダーグリッド
 */
export const CalendarGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 1,
}));

/**
 * 曜日ヘッダーセル
 */
export const WeekdayHeader = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $isSunday?: boolean; $isSaturday?: boolean }>(
  ({ theme, $isSunday, $isSaturday }) => ({
    padding: theme.spacing(1),
    textAlign: 'center',
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    color: $isSunday
      ? theme.palette.error.main
      : $isSaturday
        ? theme.palette.primary.main
        : theme.palette.text.primary,
  })
);

/**
 * 日付セル
 */
export const DayCell = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{
  $isSunday?: boolean;
  $isSaturday?: boolean;
  $isHoliday?: boolean;
  $isEmpty?: boolean;
}>(({ theme, $isSunday, $isSaturday, $isHoliday, $isEmpty }) => ({
  minHeight: 120,
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: $isEmpty
    ? theme.palette.grey[50]
    : $isHoliday || $isSunday
      ? alpha(theme.palette.error.light, 0.1)
      : $isSaturday
        ? alpha(theme.palette.primary.light, 0.1)
        : theme.palette.background.paper,
  cursor: $isEmpty ? 'default' : 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: $isEmpty
      ? theme.palette.grey[50]
      : alpha(theme.palette.primary.main, 0.1),
  },
}));

/**
 * 日付ラベル
 */
export const DateLabel = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{
  $isSunday?: boolean;
  $isSaturday?: boolean;
  $isHoliday?: boolean;
}>(({ theme, $isSunday, $isSaturday, $isHoliday }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  color: $isHoliday || $isSunday
    ? theme.palette.error.main
    : $isSaturday
      ? theme.palette.primary.main
      : theme.palette.text.primary,
}));

/**
 * 祝日ラベル
 */
export const HolidayLabel = styled(Box)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.error.main,
  marginBottom: theme.spacing(0.5),
}));

/**
 * 時間帯スロット表示
 */
export const TimeSlotItem = styled(Box)(({ theme }) => ({
  fontSize: '0.75rem',
  padding: theme.spacing(0.25, 0.5),
  marginBottom: 2,
  backgroundColor: alpha(theme.palette.grey[500], 0.1),
  borderRadius: 2,
  display: 'flex',
  justifyContent: 'space-between',
}));

/**
 * もっと見るボタン
 */
export const MoreButton = styled(Box)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  textAlign: 'center',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

/**
 * 編集可能テーブルセル（赤背景）
 */
export const EditableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.light, 0.15),
  padding: theme.spacing(0.5),
}));

/**
 * 表示専用テーブルセル
 */
export const ReadOnlyCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(0.5),
  textAlign: 'center',
}));

/**
 * ステータスチップスタイル
 */
export const StatusChipStyles = {
  draft: {
    backgroundColor: (theme: { palette: { grey: { 200: string } } }) =>
      theme.palette.grey[200],
    color: (theme: { palette: { text: { secondary: string } } }) =>
      theme.palette.text.secondary,
  },
  recruiting: {
    backgroundColor: (theme: { palette: { success: { light: string } } }) =>
      alpha(theme.palette.success.light, 0.3),
    color: (theme: { palette: { success: { dark: string } } }) =>
      theme.palette.success.dark,
  },
  closed: {
    backgroundColor: (theme: { palette: { warning: { light: string } } }) =>
      alpha(theme.palette.warning.light, 0.3),
    color: (theme: { palette: { warning: { dark: string } } }) =>
      theme.palette.warning.dark,
  },
  confirmed: {
    backgroundColor: (theme: { palette: { primary: { light: string } } }) =>
      alpha(theme.palette.primary.light, 0.3),
    color: (theme: { palette: { primary: { dark: string } } }) =>
      theme.palette.primary.dark,
  },
};

/**
 * エラーコンテナ
 */
export const ErrorContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

/**
 * ローディングオーバーレイ
 */
export const LoadingOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  zIndex: 10,
}));

/**
 * モーダルコンテンツ
 */
export const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[24],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  maxHeight: '90vh',
  overflow: 'auto',
  minWidth: 400,
}));

/**
 * ドラッグ&ドロップエリア
 */
export const DropZone = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $isDragActive?: boolean }>(
  ({ theme, $isDragActive }) => ({
    border: `2px dashed ${$isDragActive ? theme.palette.primary.main : theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    backgroundColor: $isDragActive
      ? alpha(theme.palette.primary.main, 0.05)
      : theme.palette.background.paper,
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
  })
);
